import { createContext, useContext, useState } from "react";
import { DataResult, State } from "@progress/kendo-data-query";
import { GridColumn as Column, GridDataStateChangeEvent } from "@progress/kendo-react-grid";
import { DragAndDrop } from "@progress/kendo-react-common";
import { useTheme } from "@mui/material";
import { FtlRoutingViewModel } from "../../../../ViewModel/FtlRoutingViewModel";
import StyledKendoGrid from "../../../../../Grid/StyledKendoGrid";
import InlineEditProps from "../../../../../Grid/InlineEdit/InlineEditProps";
import CommandColumn from "../../../../../Grid/ControlColumn/CommandColumn";
import { CommandDeleteRowButton } from "../../../../../Grid/ControlColumn/CommandDeleteRowButton";
import { EditProjectContext } from "../../../EditProjectContext";
import { FtlRouteViewModel } from "../../../../ViewModel/FtlRouteViewModel";
import { DragHandleCell } from "./drag-handle-cell";
import commandDeleteRow from "../../../../../Grid/ControlColumn/CommandDeleteRow";
import { isProjectReadOnly } from "../../../IsProjectReadOnly";
import { EDIT_FIELD } from "../../../../../Grid/GridConstants";
import { DraggableRowRender } from "./DraggableRowRender";
import { setChangedRoute } from "../RouteNumberArea/SetChangedRoute";
import { InitialState } from "../../../../../Grid/InitialDataState";
import { LtlPipelineViewModel } from "../../../../ViewModel/LtlPipelineViewModel";
import { isArrayUndefinedOrEmpty } from "../../../../../Utils/arrays";
import { stopDataGridColums } from "./stopDataGridColums";
import { ProjectState } from "../../../../ViewModel/ProjectState";
import { ReadOnlyProps } from "../../../ReadOnlyProps";

const idField = "ClientId";

type ContextProps = {
  reorder: (dataItem: FtlRoutingViewModel, direction: "before" | "after" | null) => void;
  dragStart: (dataItem: FtlRoutingViewModel) => void;
};

export const ReorderContext = createContext<ContextProps>({
  reorder: () => undefined,
  dragStart: () => undefined,
});

type Props = {
  routings: Array<FtlRoutingViewModel>;
  routeId: number;
};

export default function StopDataGrid({ routings, routeId, isReadOnly }: Props & ReadOnlyProps) {
  const { project, setProject, stopDataOfSpdrsOrPdsrs, setStopDataOfSpdrsOrPdsrs } =
    useContext(EditProjectContext);

  const spdrsOnThisRoute = project?.FTLRoutes?.find((x) => x.RouteId === routeId)?.SpdrsOnThisRoute;

  const pdsrsOnThisRoute = project?.FTLRoutes?.find((x) => x.RouteId === routeId)?.PdsrsOnThisRoute;

  const setSpdrsOfRouteInStopDataOfSpdr = () => {
    spdrsOnThisRoute?.forEach((spdr) => {
      if (!spdr.SpdrId) {
        return;
      }
      stopDataOfSpdrsOrPdsrs.push(spdr.SpdrId);
    });

    setStopDataOfSpdrsOrPdsrs(stopDataOfSpdrsOrPdsrs);
  };

  const setPdsrsOfRouteInStopDataOfSpdr = () => {
    pdsrsOnThisRoute?.forEach((pdsr) => {
      if (!pdsr.PdsrId) {
        return;
      }
      stopDataOfSpdrsOrPdsrs.push(pdsr.PdsrId);
    });

    setStopDataOfSpdrsOrPdsrs(stopDataOfSpdrsOrPdsrs);
  };

  const [dataState, setDataState] = useState<State>(InitialState);

  const onDataResultChanged = (dataResult: DataResult) => {
    setSpdrsOfRouteInStopDataOfSpdr();
    setPdsrsOfRouteInStopDataOfSpdr();
    setStopData(routeId, dataResult.data);
  };

  const [editState, setEditState] = useState<number>();

  const inlineEditProps = InlineEditProps({
    idField,
    dataResult: {
      data: routings,
      total: routings.length,
    },
    onDataReceived: onDataResultChanged,
    setEditState,
    isReadonly: isProjectReadOnly(project),
  });

  const projectReadOnly = isProjectReadOnly(project);
  const commandColumn = CommandColumn({
    // eslint-disable-next-line react/no-unstable-nested-components
    commands: (dataItem: any) => [
      <CommandDeleteRowButton
        disabled={projectReadOnly || isReadOnly}
        key="CommandRemoveButton"
        onClick={() =>
          commandDeleteRow({
            idField,
            dataResult: {
              data: routings,
              total: routings.length,
            },
            setDataResult: onDataResultChanged,
            dataItem,
          })
        }
      />,
    ],
    showFooterCell: !isArrayUndefinedOrEmpty(routings),
  });
  const projectContextProps = useContext(EditProjectContext);

  const setStopData = (updatedRouteId: number, updatedRoutings?: Array<FtlRoutingViewModel>) => {
    function updateFtlRoute(ftlRoute: FtlRouteViewModel) {
      if (ftlRoute.RouteId === updatedRouteId) {
        const ftlWithNewRouting = {
          ...ftlRoute,
          RoutingTable: updatedRoutings,
        };
        setChangedRoute(ftlWithNewRouting, projectContextProps).then(() => {
          const clearedPipelines = clearLtlRoutingsWhichAreUsingUpdatedRoute(ftlRoute.RouteNumber);
          setProject({
            ...project,
            ltlPipelines: clearedPipelines,
            FTLRoutes: project?.FTLRoutes?.map((route) => {
              if (route.RouteId === updatedRouteId) return ftlWithNewRouting;
              return route;
            }),
          });
        });
        return ftlWithNewRouting;
      }
      return ftlRoute;
    }

    setProject({
      ...project,
      FTLRoutes: project?.FTLRoutes?.map(updateFtlRoute),
    });
  };

  const clearLtlRoutingsWhichAreUsingUpdatedRoute = (
    ftlRouteName: string | undefined
  ): LtlPipelineViewModel[] | undefined => {
    return project.ltlPipelines?.map((pipeline: LtlPipelineViewModel) => {
      if (pipeline.routing?.map((routing) => routing.Route?.RouteName).includes(ftlRouteName)) {
        return {
          ...pipeline,
          routing: [],
          IsFinished: false,
          SupplierTransitTime: "0",
        };
      }
      return pipeline;
    });
  };

  const [activeItem, setActiveItem] = useState<FtlRoutingViewModel | null>(null);

  const getActiveItemId = () => (!activeItem ? -1 : activeItem[idField]);

  const reorder = (dataItem: any, direction: "before" | "after" | null) => {
    if (activeItem === dataItem) {
      return;
    }

    const reorderedData = routings.slice();
    const prevIndex = reorderedData.findIndex((p) => p[idField] === getActiveItemId());
    const nextIndex = reorderedData.findIndex((p) => p[idField] === dataItem[idField]);
    reorderedData.splice(prevIndex, 1);
    reorderedData.splice(
      Math.max(nextIndex + (direction === "before" ? -1 : 0), 0),
      0,
      activeItem || reorderedData[0]
    );
    setSpdrsOfRouteInStopDataOfSpdr();
    setStopData(routeId, reorderedData);
  };

  const dragStart = (dataItem: FtlRoutingViewModel) => {
    setActiveItem(dataItem);
  };

  const theme = useTheme();
  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <ReorderContext.Provider value={{ reorder, dragStart }}>
      <DragAndDrop>
        <StyledKendoGrid
          muiTheme={theme}
          resizable
          data={{
            data: routings.withEditfield(idField, editState),
            total: routings.length,
          }}
          dataItemKey={idField}
          {...dataState}
          onDataStateChange={(e: GridDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}
          {...inlineEditProps}
          /* eslint-disable-next-line react/no-unstable-nested-components */
          rowRender={(tr, props) => (
            <DraggableRowRender
              originalProps={props}
              tr={tr}
              editField={EDIT_FIELD}
              exitEdit={() => {
                setEditState(undefined);
              }}
            />
          )}
        >
          {!projectReadOnly && !isReadOnly ? (
            <Column title="" width="40px" cell={DragHandleCell} />
          ) : (
            <div />
          )}
          {[
            stopDataGridColums({
              routings,
              stopDataIsEditable: project.State === ProjectState.Sandbox,
              isReadOnly,
            }),
            commandColumn,
          ]}
        </StyledKendoGrid>
      </DragAndDrop>
    </ReorderContext.Provider>
  );
}
